import {
  Container,
  Title,
  Text,
  Paper,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Checkbox,
  Grid,
  Stack,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { type DateValue, DateInput } from '@mantine/dates';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { Department, CreateVolunteerDto } from '../../api/volunteer';
import { useNavigate } from 'react-router-dom';
import { storeVolunteerSession } from '../../lib/volunteerSession';
import { EnumButtonGroup } from '@/components/EnumButton';

dayjs.extend(customParseFormat);

function parseBirthDate(value: string): string | null {
  const parsedDate = dayjs(value, 'DD.MM.YYYY', true);
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
}

interface VolunteerFormData {
  firstName: string;
  lastName: string;
  birthDate: DateValue;
  email: string;
  phone: string;
  address: string;
  zipCode: string;
  city: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelation: string;
  motivation: string;
  experience: string;
  languages: string;
  department: Department[];
  workingHoursPerMonth: number;
  dataProtectionConsent: boolean;
}

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const form = useForm<VolunteerFormData>({
    initialValues: {
      firstName: '',
      lastName: '',
      birthDate: null,
      email: '',
      phone: '',
      address: '',
      zipCode: '',
      city: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactRelation: '',
      motivation: '',
      experience: '',
      department: [],
      workingHoursPerMonth: 0,
      languages: '',
      dataProtectionConsent: false,
    },
    validate: {
      firstName: value =>
        value.length < 2
          ? t('volunteerForm.validation.firstNameRequired')
          : null,
      lastName: value =>
        value.length < 2
          ? t('volunteerForm.validation.lastNameRequired')
          : null,
      email: value =>
        /^\S+@\S+$/.test(value)
          ? null
          : t('volunteerForm.validation.invalidEmail'),
      birthDate: value =>
        !value ? t('volunteerForm.validation.birthDateRequired') : null,

      phone: value =>
        value.length < 8 ? t('volunteerForm.validation.phoneRequired') : null,
      department: value =>
        value.length === 0
          ? t('volunteerForm.validation.departmentRequired')
          : null,
      workingHoursPerMonth: value =>
        value <= 0 ? t('volunteerForm.validation.workingHoursRequired') : null,
      address: value =>
        value?.trim() === ''
          ? t('volunteerForm.validation.addressRequired')
          : null,
      city: value =>
        value?.trim() === ''
          ? t('volunteerForm.validation.cityRequired')
          : null,
      zipCode: value =>
        /^\d{5}$/.test(value)
          ? null
          : t('volunteerForm.validation.invalidZipCode'),
      dataProtectionConsent: value =>
        !value ? t('volunteerForm.validation.dataProtectionRequired') : null,
    },
  });

  const handleSubmit = async (values: VolunteerFormData) => {
    try {
      // Transform the form data to match API expectations

      if (!values.birthDate || !values.address) return;
      const payload: CreateVolunteerDto = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        birthDate: values.birthDate
          ? typeof values.birthDate === 'object' &&
            'toISOString' in values.birthDate
            ? values.birthDate.toISOString()
            : new Date(values.birthDate).toISOString()
          : new Date(values.birthDate).toISOString(),
        address: values.address,
        zipCode: values.zipCode,
        city: values.city,
        emergencyContactName: values.emergencyContactName,
        emergencyContactPhone: values.emergencyContactPhone,
        emergencyContactRelation: values.emergencyContactRelation,
        motivation: values.motivation,
        experience: values.experience,
        department: values.department,
        workingHoursPerMonth: parseInt(
          values.workingHoursPerMonth as unknown as string,
          10
        ),
        weekdaysAvailable: [],
        languages: values.languages,
        dataProtectionConsent: values.dataProtectionConsent,
      };

      // Keep the application local until the Auth0 account is created.
      const registrationId = storeVolunteerSession(payload);
      navigate(`/volunteer/sign?volunteerId=${registrationId}`);

    } catch (error) {
      console.error('Registration failed:', error);
    }
  };

  const departmentOptions: { value: Department; label: string }[] = [
    { value: 'DISTRIBUTION', label: t('departments.distribution') },
    { value: 'LOGISTICS', label: t('departments.logistics') },
    { value: 'COOKING', label: t('departments.cooking') },
    { value: 'STOREFRONT', label: t('departments.storefront') },
  ];

  const relationOptions = [
    t('volunteerForm.emergencyContact.relations.partner'),
    t('volunteerForm.emergencyContact.relations.mother'),
    t('volunteerForm.emergencyContact.relations.father'),
    t('volunteerForm.emergencyContact.relations.sibling'),
    t('volunteerForm.emergencyContact.relations.child'),
    t('volunteerForm.emergencyContact.relations.friend'),
    t('volunteerForm.emergencyContact.relations.other'),
  ];

  return (
    <Container size="lg" py="xl">
      <Paper shadow="md" p="xl">
        <Title order={1} mb="md" ta="center">
          {t('volunteerForm.title')}
        </Title>
        <Text size="md" mb="xl" ta="center" c="dimmed">
          {t('volunteerForm.subtitle')}
        </Text>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="xl">
            {/* Personal Information */}
            <div>
              <Title order={3} mb="md">
                {t('volunteerForm.personalInfo.title')}
              </Title>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label={`${t('volunteerForm.personalInfo.firstName')} *`}
                    placeholder={t(
                      'volunteerForm.personalInfo.placeholders.firstName'
                    )}
                    {...form.getInputProps('firstName')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label={`${t('volunteerForm.personalInfo.lastName')} *`}
                    placeholder={t(
                      'volunteerForm.personalInfo.placeholders.lastName'
                    )}
                    {...form.getInputProps('lastName')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <DateInput
                    label={`${t('volunteerForm.personalInfo.birthDate')} *`}
                    placeholder={t(
                      'volunteerForm.personalInfo.placeholders.birthDate'
                    )}
                    valueFormat="DD.MM.YYYY"
                    dateParser={parseBirthDate}
                    {...form.getInputProps('birthDate')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label={`${t('volunteerForm.personalInfo.phone')} *`}
                    placeholder={t(
                      'volunteerForm.personalInfo.placeholders.phone'
                    )}
                    {...form.getInputProps('phone')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 12 }}>
                  <TextInput
                    label={`${t('volunteerForm.personalInfo.email')} *`}
                    placeholder={t(
                      'volunteerForm.personalInfo.placeholders.email'
                    )}
                    {...form.getInputProps('email')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 12 }}>
                  <TextInput
                    label={`${t('volunteerForm.personalInfo.address')} *`}
                    placeholder={t(
                      'volunteerForm.personalInfo.placeholders.address'
                    )}
                    {...form.getInputProps('address')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label={`${t('volunteerForm.personalInfo.zipCode')} *`}
                    placeholder={t(
                      'volunteerForm.personalInfo.placeholders.zipCode'
                    )}
                    {...form.getInputProps('zipCode')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label={`${t('volunteerForm.personalInfo.city')} *`}
                    placeholder={t(
                      'volunteerForm.personalInfo.placeholders.city'
                    )}
                    {...form.getInputProps('city')}
                  />
                </Grid.Col>
              </Grid>
            </div>

            {/* Emergency Contact */}
            <div>
              <Title order={3} mb="md">
                {t('volunteerForm.emergencyContact.title')}
              </Title>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <TextInput
                    label={t('volunteerForm.emergencyContact.name')}
                    placeholder={t(
                      'volunteerForm.emergencyContact.placeholders.name'
                    )}
                    {...form.getInputProps('emergencyContactName')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <TextInput
                    label={t('volunteerForm.emergencyContact.phone')}
                    placeholder={t(
                      'volunteerForm.emergencyContact.placeholders.phone'
                    )}
                    {...form.getInputProps('emergencyContactPhone')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Select
                    label={t('volunteerForm.emergencyContact.relation')}
                    placeholder={t(
                      'volunteerForm.emergencyContact.placeholders.relation'
                    )}
                    data={relationOptions}
                    {...form.getInputProps('emergencyContactRelation')}
                  />
                </Grid.Col>
              </Grid>
            </div>

            {/* Volunteer Information */}
            <div>
              <Title order={3} mb="md">
                {t('volunteerForm.volunteerInfo.title')}
              </Title>
              <Stack gap="md">
                <Textarea
                  label={t('volunteerForm.volunteerInfo.motivation')}
                  placeholder={t(
                    'volunteerForm.volunteerInfo.placeholders.motivation'
                  )}
                  rows={3}
                  {...form.getInputProps('motivation')}
                />
                <Textarea
                  label={t('volunteerForm.volunteerInfo.experience')}
                  placeholder={t(
                    'volunteerForm.volunteerInfo.placeholders.experience'
                  )}
                  rows={3}
                  {...form.getInputProps('experience')}
                />
                <EnumButtonGroup<Department>
                  label={`${t('volunteerForm.volunteerInfo.department')} *`}
                  options={departmentOptions}
                  value={form.values.department}
                  onChange={value => form.setFieldValue('department', value)}
                />
                <TextInput
                  label={`${t('volunteerForm.volunteerInfo.workingHoursPerMonth')} *`}
                  placeholder={t(
                    'volunteerForm.volunteerInfo.placeholders.workingHoursPerMonth'
                  )}
                  type="number"
                  {...form.getInputProps('workingHoursPerMonth')}
                />
                <TextInput
                  label={t('volunteerForm.volunteerInfo.languages')}
                  placeholder={t(
                    'volunteerForm.volunteerInfo.placeholders.languages'
                  )}
                  {...form.getInputProps('languages')}
                />
              </Stack>
            </div>

            {/* Legal Agreements */}
            <div>
              <Title order={3} mb="md">
                {t('volunteerForm.legal.title')}
              </Title>
              <Stack gap="md">
                <Checkbox
                  label={
                    <Trans
                      i18nKey="volunteerForm.legal.dataProtection"
                      components={[
                        <a href="https://strassenwaechter-ev.de/datenschutz/" />,
                      ]}
                    />
                  }
                  {...form.getInputProps('dataProtectionConsent', {
                    type: 'checkbox',
                  })}
                />
              </Stack>
            </div>

            {/* Submit Button */}
            <Group justify="center" mt="xl">
              <Button
                type="submit"
                size="lg"
                variant="filled"
              >
                {t('common.next')}
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
